<section class="ft-master-section ft-campaign-master">
  <!-- The native pending-changes panel (editing) — #52 recipe. inactive = native campaignForm.$valid. -->
  @if (!model().isCreating) {
    <ft-pending-changes-panel-ng
      #panel
      [live]="model().campaign"
      [inactive]="!campaignValid()"
      (save)="onPanelSave()"
      (cancelled)="onPanelCancel()"
     />
  }

  <!-- The isCreating "set up a new variation" alert (gate-blind; the v1 ft-pending-changes inline form). -->
  @if (model().isCreating) {
    <div class="ft-alert-section is-actionable">
      <div class="ft-alert">
        <div class="ft-alert-content">
          <h3 class="ft-heading">{{ 'flipto.account.SetUpANewVariation' | translate }}</h3>
          <p>{{ 'flipto.account.OnceYouSaveThisVariation' | translate }}</p>
          <div class="ft-alert-actions">
            <a class="alert-confirm save ft-btn ft-btn-plus-1 ft-btn-default" [class.disabled]="!createValid()" (click)="onCreateSave()">{{ 'flipto.account.SaveVariation' | translate }}</a>
            <a class="alert-cancel cancel ft-btn ft-btn-plus-1 ft-btn-text" (click)="cancel()">{{ 'flipto.account.Cancel' | translate }}</a>
          </div>
        </div>
      </div>
    </div>
  }

  <section class="ft-section ft-config-section">
    <div class="ft-container">

      <!-- INFO (Custom campaigns only) -->
      @if (isCustomOrCreating) {
        <div class="ft-row">
          <div class="ft-col-33">
            <div class="ft-section-header">
              <div class="ft-label">{{ 'flipto.account.platform.preStay.campaign.Info' | translate }}</div>
            </div>
          </div>
          <div class="ft-col-66">
            <div class="ft-panel last" ft-modal-fields-editor (click)="editName()">
              <div class="ft-row">
                <div class="ft-col-33">
                  <div class="ft-label">{{ 'flipto.account.VariationName' | translate }}: </div>
                </div>
                <div class="ft-col-66">
                  <div>{{ model().campaign?.name }}@if (!model().campaign?.name) {<span class="placeholder">{{ 'flipto.account.NoneSet' | translate }}</span>}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      }

      <!-- CRITERIA (Custom campaigns only) -->
      @if (isCustomOrCreating) {
        <div class="ft-row">
          <div class="ft-col-33">
            <div class="ft-section-header">
              <div class="ft-label">{{ 'flipto.account.platform.preStay.campaign.Criteria' | translate }}</div>
            </div>
          </div>
          <div class="ft-col-66">
            <div class="ft-panel last" ft-targets-modal="editCriteriaModal" (click)="showCriteriaModal()">
              <div class="ft-row">
                <div class="ft-col-33">
                  <div class="ft-label">{{ 'flipto.account.platform.preStay.campaign.RoomType' | translate }}: </div>
                </div>
                <div class="ft-col-66">
                  <div>{{ roomTypesText }}@if (!roomTypesText) {<span class="placeholder">{{ 'flipto.account.NoneSet' | translate }}</span>}</div>
                </div>
              </div>
              <div class="ft-row">
                <div class="ft-col-33">
                  <div class="ft-label">{{ 'flipto.account.platform.preStay.campaign.DateRange' | translate }}: </div>
                </div>
                <div class="ft-col-66">
                  @for (dateRange of dateRanges; track dateRange; let last = $last) {
                    <span>
                      {{ monthName(dateRange.startMonth) }} {{ dateRange.startDay }} - {{ monthName(dateRange.endMonth) }} {{ dateRange.endDay }}{{ last ? '' : ', ' }}
                    </span>
                  }
                  @if (dateRanges.length === 0) {
                    <div><span class="placeholder">{{ 'flipto.account.NoneSet' | translate }}</span></div>
                  }
                </div>
              </div>
              <div class="ft-row">
                <div class="ft-col-33">
                  <div class="ft-label">{{ 'flipto.account.platform.preStay.campaign.LengthOfStay' | translate }}: </div>
                </div>
                <div class="ft-col-66">
                  @for (stayLength of stayLengths; track stayLength; let last = $last) {
                    <span>{{ nightsText(stayLength.minNights) }} - {{ nightsText(stayLength.maxNights) }}{{ last ? '' : ', ' }}</span>
                  }
                  @if (stayLengths.length === 0) {
                    <div><span class="placeholder">{{ 'flipto.account.NoneSet' | translate }}</span></div>
                  }
                </div>
              </div>
              <div class="ft-row">
                <div class="ft-col-33">
                  <div class="ft-label">{{ 'flipto.account.platform.preStay.campaign.Languages' | translate }}: </div>
                </div>
                <div class="ft-col-66">
                  <div>{{ criteriaLanguagesText }}@if (!criteriaLanguagesText) {<span class="placeholder">{{ 'flipto.account.NoneSet' | translate }}</span>}</div>
                </div>
              </div>
              <div class="ft-row">
                <div class="ft-col-33">
                  <div class="ft-label">{{ 'flipto.account.platform.preStay.campaign.RateCode' | translate }}: </div>
                </div>
                <div class="ft-col-66">
                  <div>{{ rateCodesText }}@if (!rateCodesText) {<span class="placeholder">{{ 'flipto.account.NoneSet' | translate }}</span>}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      }

      <!-- SHARE MESSAGE -->
      <div class="ft-row">
        <div class="ft-col-33">
          <div class="ft-section-header">
            <div class="ft-label">{{ 'flipto.account.platform.preStay.campaign.ShareMessage' | translate }}</div>
            <div class="ft-summary">
              <ft-language-selector-ng [languages]="model().languages" [selectedLanguage]="selectedLanguage()"
                                       (selectedLanguageChange)="selectedLanguage.set($event)" />
            </div>
          </div>
        </div>
        <div class="ft-col-66">
          @for (message of orderedMessages; track message) {
            <div class="ft-panel"
                 [class.disabled]="!parseBool(message.isActive)"
                 (click)="editMessage(message)">
              <div class="ft-row">
                <div class="ft-col-75">
                  <span>{{ messageText(message) }}@if (!messageText(message)) {<span class="placeholder">{{ 'flipto.account.NoneSet' | translate }}</span>}</span>
                </div>
              </div>
              <ft-panel-status>@if (!parseBool(message.isActive)) {<div class="panel-status">Off</div>}</ft-panel-status>
            </div>
          }
          <button class="ft-btn ft-btn-outline-dark" (click)="addMessage()" value="+">
            <i class="ft-icn-plus"></i> {{ 'flipto.account.AddMessageVariation' | translate }}
          </button>
        </div>
      </div>

      <!-- GOALS -->
      <div class="ft-row">
        <div class="ft-col-33">
          <div class="ft-section-header">
            <div class="ft-label">{{ 'flipto.account.platform.preStay.campaign.Goals' | translate }}</div>
            <p class="ft-summary">{{ 'flipto.account.platform.preStay.campaign.GoalsSummary' | translate }}</p>
          </div>
        </div>
        <div class="ft-col-66">
          <ft-campaign-goals-ng [campaign]="model().campaign" [propertyIncentives]="propertyIncentives()"
                                [culture]="selectedLanguage()" [layer]="layerName()"
                                (editGoals)="onEditGoals($event)" />
        </div>
      </div>

    </div>
  </section>
</section>

<!-- INFO name editor (native field-editor modal). DEFERRED: on close → panel.recompute(). -->
<ft-field-editor-modal [config]="nameFieldConfig()" (closed)="onNameClosed()" />

<!-- SHARE MESSAGE editor (native). v1 prestay.editMessageModal: ft-snapshot="model.campaign.messages",
     on-cancel="editMessageCancel"; inline ft-switch + ft-html-field (text mode, tokens) + delete. -->
<ft-modal-overlay [open]="messageModalOpen()">
  @if (messageModalOpen()) {
    <ft-modal-chrome-ng
      size="sm"
      [doneVisible]="true"
      [cancelVisible]="true"
      [footerVisible]="true"
      [footerCancelVisible]="false"
      (done)="onMessageDone()"
      (cancel)="onMessageCancel()"
    >
      <div class="modal-header">
        <div class="modal-heading">
          {{ 'flipto.account.platform.preStay.campaign.ShareMessage' | translate }}
          @if (!messageSwitchHidden()) {
            <div class="ft-switch">
              <input class="ft-switch-toggle" type="checkbox" [id]="messageSwitchUid" [checked]="model().selectedMessage?.isActive === true" (change)="onMessageActiveToggle($event)" />
              <label [attr.for]="messageSwitchUid"></label>
            </div>
          }
        </div>
      </div>

      <ft-html-field-ng [datasource]="model().selectedMessage?.languages" [fields]="messageFields" [fieldsOptions]="messageFieldsOptions" />

      <div class="modal-footer-align">
        @if (!messageSwitchHidden()) {
          <a class="ft-btn ft-btn-plus-1 ft-btn-outline-light" (click)="deleteMessage(model().selectedMessage)">
            {{ 'flipto.account.DeleteMessage' | translate }}
          </a>
        }
      </div>
    </ft-modal-chrome-ng>
  }
</ft-modal-overlay>

<!-- CRITERIA editor (native). v1 editCriteriaModal: ft-snapshot="model.campaign", 5 panels. -->
<ft-modal-overlay [open]="criteriaModalOpen()">
  @if (criteriaModalOpen()) {
    <ft-modal-chrome-ng
      [headerText]="'flipto.account.platform.preStay.campaign.Criteria' | translate"
      size="sm"
      [doneVisible]="true"
      [doneDisabled]="criteriaDoneDisabled()"
      [cancelVisible]="true"
      [footerVisible]="true"
      [footerCancelVisible]="false"
      (done)="onCriteriaDone()"
      (cancel)="onCriteriaCancel()"
    >
      <!-- ROOM TYPE -->
      <div class="modal-panel criteria-editor">
        <div class="ft-label">{{ 'flipto.account.platform.preStay.campaign.RoomType' | translate }}</div>
        <div class="modal-panel-actions">
          <a class="action btn-add-item" (click)="model().campaign.criteria.roomTypes.item.push({})"></a>
        </div>
        @for (roomType of reversed(model().campaign?.criteria?.roomTypes?.item); track roomType; let last = $last) {
          <div>
            <div class="list-item">
              <input type="text" [(ngModel)]="roomType.type" [ngModelOptions]="{ standalone: true }" placeholder="Enter a room type" />
              <a class="btn-delete-item" (click)="removeItemFrom(roomType, model().campaign.criteria.roomTypes.item)"></a>
            </div>
            @if (!last) {
              <div class="or"><span>{{ 'flipto.account.platform.preStay.campaign.Or' | translate }}</span></div>
            }
          </div>
        }
      </div>

      <!-- DATE RANGE -->
      <div class="modal-panel criteria-editor">
        <div class="ft-label">{{ 'flipto.account.platform.preStay.campaign.DateRange' | translate }}</div>
        <div class="modal-panel-actions">
          <a class="action btn-add-item" ftDateRangeNg [options]="addRangeOptions" (dateRangeChanged)="addNewRange($event)"></a>
        </div>
        @for (dateRange of reversed(model().campaign?.criteria?.dateRanges?.item); track dateRange; let last = $last) {
          <div>
            <div class="list-item">
              <div class="ft-dropdown"
                   (click)="selectDateRange(dateRange)"
                   ftDateRangeNg
                   [options]="editRangeOptions(dateRange)"
                   (dateRangeChanged)="updateDateRange($event)">
                {{ monthName(dateRange.startMonth) }} {{ dateRange.startDay }} - {{ monthName(dateRange.endMonth) }} {{ dateRange.endDay }} <i class="ft-icn-caret-down"></i>
              </div>
              <a class="btn-delete-item" (click)="removeItemFrom(dateRange, model().campaign.criteria.dateRanges.item)"></a>
            </div>
            @if (!last) {
              <div class="or"><span>{{ 'flipto.account.platform.preStay.campaign.Or' | translate }}</span></div>
            }
          </div>
        }
      </div>

      <!-- LENGTH OF STAY -->
      <div class="modal-panel criteria-editor">
        <div class="ft-label">{{ 'flipto.account.platform.preStay.campaign.LengthOfStay' | translate }}</div>
        <div class="modal-panel-actions">
          <a class="action btn-add-item" (click)="model().campaign.criteria.stayLengths.item.push({})"></a>
        </div>
        @for (stayLength of reversed(model().campaign?.criteria?.stayLengths?.item); track stayLength; let last = $last) {
          <div>
            <div class="list-item">
              <div class="ft-dropdown" [class.active]="isDropdownOpen('min', stayLength)">
                <a class="ft-dropdown-toggle" (click)="toggleDropdown('min', stayLength, $event)"><span>{{ nightsText(stayLength.minNights) }} {{ ('flipto.account.platform.preStay.campaign.Min' | translate) | lowercase }}</span> <i class="ft-icn-caret-down"></i></a>
                <ul class="ft-dropdown-menu">
                  @for (length of minLengthOptions(stayLength); track length) {
                    <li (click)="stayLength.minNights = length; closeDropdowns(); $event.stopPropagation()">
                      <a [class.active]="length === stayLength.minNights">{{ length }}</a>
                    </li>
                  }
                </ul>
              </div>
              <span class="separator">-</span>
              <div class="ft-dropdown" [class.active]="isDropdownOpen('max', stayLength)">
                <a class="ft-dropdown-toggle" (click)="toggleDropdown('max', stayLength, $event)"><span>{{ nightsText(stayLength.maxNights) }} {{ ('flipto.account.platform.preStay.campaign.Max' | translate) | lowercase }}</span> <i class="ft-icn-caret-down"></i></a>
                <ul class="ft-dropdown-menu">
                  @for (length of maxLengthOptions(stayLength); track length) {
                    <li (click)="stayLength.maxNights = length; closeDropdowns(); $event.stopPropagation()">
                      <a [class.active]="length === stayLength.maxNights">{{ length }}</a>
                    </li>
                  }
                  <li (click)="stayLength.maxNights = 99; closeDropdowns(); $event.stopPropagation()">
                    <a [class.active]="99 === stayLength.maxNights">{{ 'flipto.account.platform.preStay.campaign.MoreThan7' | translate }}</a>
                  </li>
                </ul>
              </div>
              <a class="btn-delete-item" (click)="removeItemFrom(stayLength, model().campaign.criteria.stayLengths.item)"></a>
            </div>
            @if (!last) {
              <div class="or"><span>{{ 'flipto.account.platform.preStay.campaign.Or' | translate }}</span></div>
            }
          </div>
        }
      </div>

      <!-- LANGUAGES -->
      <div class="modal-panel criteria-editor">
        <div class="ft-label">{{ 'flipto.account.platform.preStay.campaign.Languages' | translate }}</div>
        <div class="modal-panel-actions">
          <div class="action ft-dropdown" [class.active]="isDropdownOpen('lang', null)">
            <a class="ft-dropdown-toggle btn-add-item" (click)="toggleDropdown('lang', null, $event)"></a>
            <ul class="ft-dropdown-menu">
              @for (language of addableLanguages(); track language) {
                <li (click)="model().campaign.criteria.languages.item.push(language); closeDropdowns(); $event.stopPropagation()">
                  <a>{{ language.name }}</a>
                </li>
              }
            </ul>
          </div>
        </div>
        @for (language of reversed(model().campaign?.criteria?.languages?.item); track language; let last = $last) {
          <div>
            <div class="list-item">
              <span>{{ language.name }}</span>
              <a class="btn-delete-item" (click)="removeItemFrom(language, model().campaign.criteria.languages.item)"></a>
            </div>
            @if (!last) {
              <div class="or"><span>{{ 'flipto.account.platform.preStay.campaign.Or' | translate }}</span></div>
            }
          </div>
        }
      </div>

      <!-- RATE CODE -->
      <div class="modal-panel criteria-editor">
        <div class="ft-label">{{ 'flipto.account.platform.preStay.campaign.RateCode' | translate }}</div>
        <div class="modal-panel-actions">
          <a class="action btn-add-item" (click)="model().campaign.criteria.rateCodes.item.push({})"></a>
        </div>
        @for (rateCode of reversed(model().campaign?.criteria?.rateCodes?.item); track rateCode; let last = $last) {
          <div>
            <div class="list-item">
              <input type="text" [(ngModel)]="rateCode.code" [ngModelOptions]="{ standalone: true }" placeholder="Enter a rate code" />
              <a class="btn-delete-item" (click)="removeItemFrom(rateCode, model().campaign.criteria.rateCodes.item)"></a>
            </div>
            @if (!last) {
              <div class="or"><span>{{ 'flipto.account.platform.preStay.campaign.Or' | translate }}</span></div>
            }
          </div>
        }
      </div>
    </ft-modal-chrome-ng>
  }
</ft-modal-overlay>

<!-- GOALS editor (native — #72 post-stay recipe). DEFERRED: Done → panel.recompute(). -->
<ft-modal-overlay [open]="goalsModalOpen()">
  @if (goalsModalOpen()) {
    <ft-modal-chrome-ng
      [headerText]="types() && types().length ? (types()[0] | editGoalsTitle: layerName()) : ''"
      size="sm"
      [doneVisible]="true"
      [cancelVisible]="true"
      [footerVisible]="true"
      [footerCancelVisible]="false"
      (done)="onGoalsDone()"
      (cancel)="onGoalsCancel()"
    >
      <ft-edit-campaign-goals-ng [goals]="model().campaign?.goals?.item"
        [incentives]="propertyIncentives()"
        [culture]="selectedLanguage()"
        [types]="types()"
        [incentiveTag]="incentiveTag()"
       />
    </ft-modal-chrome-ng>
  }
</ft-modal-overlay>
